import { useMemo, useState } from "react";
import moment from "moment";
import { ResponsiveBar } from "@nivo/bar";
import { Show } from "shared/components";
import { Card } from "shared/components/Card";
import { Loader } from "shared/components/Loader";
import { cx } from "shared/utils";
import { useMedia } from "shared/providers/media-query/use-media";
import type { LeadsOverTimeResponse } from "api/crm-analytics";
import { getXTickValues } from "../utils";
import { AnalyticsEmptyState } from "./AnalyticsEmptyState";
import { ChartTooltip } from "./ChartTooltip";
import { FALLBACK_COLOR } from "./modules/constants/colors";
import { CHART_THEME } from "./modules/constants/chartTheme";

// Sources the API gives no colour for — every band still has to be visually
// distinct, so fall back to a fixed rotation rather than repeating grey.
const SOURCE_PALETTE = [
  "#E6007E",
  "#2D9CDB",
  "#52C535",
  "#F4C542",
  "#9B51E0",
  "#F2994A",
  "#00BFA5",
  "#FE5DB5"
];

const UNATTRIBUTED_LABEL = "Unknown";
// The unattributed band is deliberately the one neutral colour: it is an
// absence of data, not another channel, and should not compete with the real
// sources for attention.
const UNATTRIBUTED_COLOR = FALLBACK_COLOR;

// Matches the reference dashboard: a bar per day up to a month, per week out to
// a quarter, per month beyond that. Bucketing here rather than server-side
// keeps the endpoint free of presentation choices — it always sends days.
const WEEKLY_FROM_DAYS = 31;
const MONTHLY_FROM_DAYS = 91;

type Bucket = "day" | "week" | "month";

const bucketFor = (days: number): Bucket => {
  if (days >= MONTHLY_FROM_DAYS) return "month";
  if (days >= WEEKLY_FROM_DAYS) return "week";
  return "day";
};

const BUCKET_FORMAT: Record<Bucket, string> = {
  day: "MMM D",
  week: "MMM D",
  month: "MMM YYYY"
};

// A source's stack key. Numeric ids would collide with nivo's own indexing, and
// the unattributed band has no id at all, so both are namespaced.
const seriesKey = (sourceId: number | null) =>
  sourceId === null ? "source_none" : `source_${sourceId}`;

export const LeadsOverTimeCard = ({
  data,
  isLoading,
  dateLabel,
  startDate,
  endDate
}: {
  data: LeadsOverTimeResponse | undefined;
  isLoading: boolean;
  dateLabel: string;
  startDate: string;
  endDate: string;
}) => {
  const { isMobile } = useMedia();
  // Legend entries the viewer has switched off. Kept as keys rather than
  // indices so it survives the series list changing under a filter change.
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const series = useMemo(
    () =>
      (data?.sources ?? []).map((source, index) => ({
        key: seriesKey(source.source_id),
        label: source.name ?? UNATTRIBUTED_LABEL,
        color:
          source.source_id === null
            ? UNATTRIBUTED_COLOR
            : (source.color ??
              SOURCE_PALETTE[index % SOURCE_PALETTE.length] ??
              FALLBACK_COLOR)
      })),
    [data?.sources]
  );

  const bucket = useMemo(() => {
    const days =
      moment(endDate)
        .startOf("day")
        .diff(moment(startDate).startOf("day"), "days") + 1;
    return bucketFor(days);
  }, [startDate, endDate]);

  // One row per bucket across the whole range, including buckets the API sent
  // no rows for — a gap in the bars means "no leads", and dropping the bucket
  // entirely would silently compress the time axis instead.
  const rows = useMemo(() => {
    const start = moment(startDate).startOf(bucket);
    const end = moment(endDate).startOf(bucket);
    const total = end.diff(start, bucket) + 1;

    const byBucket = new Map<string, Record<string, number>>();
    (data?.daily ?? []).forEach((point) => {
      const stamp = moment(point.date).startOf(bucket).format("YYYY-MM-DD");
      const entry = byBucket.get(stamp) ?? {};
      const key = seriesKey(point.source_id);
      entry[key] = (entry[key] ?? 0) + point.count;
      byBucket.set(stamp, entry);
    });

    return Array.from({ length: Math.max(total, 0) }, (_, index) => {
      const at = start.clone().add(index, bucket);
      const counts = byBucket.get(at.format("YYYY-MM-DD")) ?? {};
      const row: Record<string, string | number> = {
        date: at.format(BUCKET_FORMAT[bucket])
      };
      series.forEach((entry) => {
        row[entry.key] = hidden.has(entry.key) ? 0 : (counts[entry.key] ?? 0);
      });
      return row;
    });
  }, [data?.daily, series, hidden, bucket, startDate, endDate]);

  const colorByKey = useMemo(
    () => Object.fromEntries(series.map((entry) => [entry.key, entry.color])),
    [series]
  );

  const xTickValues = useMemo(
    () =>
      getXTickValues(
        rows.map((row) => String(row.date)),
        isMobile ? 5 : 11
      ),
    [rows, isMobile]
  );

  const toggle = (key: string) =>
    setHidden((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const hasData = (data?.total_leads ?? 0) > 0;

  return (
    <Card className="p-6 w-full h-full flex flex-col">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <p className="text-base font-medium text-content-700">
            Leads over time
          </p>
          <p className="text-sm font-medium text-content-300">{dateLabel}</p>
        </div>

        <Show if={!isLoading && hasData}>
          <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
            {series.map((entry) => (
              <button
                key={entry.key}
                type="button"
                aria-pressed={!hidden.has(entry.key)}
                onClick={() => toggle(entry.key)}
                className={cx(
                  "flex shrink-0 items-center gap-2 whitespace-nowrap text-xs font-medium",
                  hidden.has(entry.key)
                    ? "text-content-300"
                    : "text-content-1000"
                )}
              >
                <span
                  className="inline-block h-3 w-3 shrink-0 rounded-full border border-alpha-100"
                  style={{
                    backgroundColor: hidden.has(entry.key)
                      ? "transparent"
                      : entry.color,
                    borderColor: entry.color
                  }}
                />
                {entry.label}
              </button>
            ))}
          </div>
        </Show>
      </div>

      <Show if={isLoading}>
        <div className="flex min-h-[260px] items-center justify-center">
          <Loader size={24} />
        </div>
      </Show>

      <Show if={!isLoading && !hasData}>
        <AnalyticsEmptyState description="There are no data to show for the selected filter" />
      </Show>

      <Show if={!isLoading && hasData}>
        <div className="mt-6 flex-1 min-h-[260px]">
          <ResponsiveBar
            data={rows}
            keys={series.map((entry) => entry.key)}
            indexBy="date"
            groupMode="stacked"
            margin={{ top: 16, right: 8, bottom: 28, left: 40 }}
            padding={0.3}
            colors={({ id }) => colorByKey[id as string] ?? FALLBACK_COLOR}
            theme={CHART_THEME}
            axisLeft={{ tickSize: 0, tickPadding: 8 }}
            axisBottom={{
              tickSize: 0,
              tickPadding: 8,
              tickValues: xTickValues
            }}
            enableGridY
            enableLabel={false}
            animate={false}
            // The whole bucket, not the band under the pointer. nivo hands over
            // the hovered segment, but the question a stacked bar gets asked is
            // "what made up this day" — one band's number cannot answer it, and
            // reads as the day having had only that one source.
            //
            // Bands at zero are left out rather than listed: with up to eight
            // channels most are absent on any given day, and a column of zeroes
            // buries the ones that are not. A band hidden from the legend is
            // already zeroed into `rows`, so it drops out here too.
            tooltip={({ indexValue, data }) => {
              const present = series.filter(
                (entry) => Number(data[entry.key] ?? 0) > 0
              );
              const total = present.reduce(
                (sum, entry) => sum + Number(data[entry.key] ?? 0),
                0
              );

              return (
                <ChartTooltip
                  title={String(indexValue)}
                  subtitle={`${total.toLocaleString("en-US")} ${
                    total === 1 ? "lead" : "leads"
                  }`}
                  rows={present.map((entry) => ({
                    label: entry.label ?? UNATTRIBUTED_LABEL,
                    value: Number(data[entry.key]).toLocaleString("en-US"),
                    color: colorByKey[entry.key] ?? FALLBACK_COLOR
                  }))}
                />
              );
            }}
          />
        </div>
      </Show>
    </Card>
  );
};
